import React from 'react';

import { DndContext, useDroppable } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import cx from 'classnames';
import { isEmpty } from 'ramda';

import { ETimelineType, ETripEvent } from '../../../../../../../detailsv2/structure';
import { EVENT_OPTION_CONFIG } from '../Event/config';
import { EventOption } from '../Event/EventOption';
import { TDayTripEvent } from '../types';
import { validateTripEvent } from '../utils';
import { useEventList } from './functions';
import s from './index.module.css';
import { IDraggableItemProps, ITransform } from './types';

export const EventList = ({ events, highlighted }: { events: TDayTripEvent[]; highlighted?: boolean }) => {
  const { setNodeRef } = useDroppable({
    id: 'event-list-droppable',
  });
  const { handleDragEnd } = useEventList({ events });

  return (
    <DndContext onDragEnd={handleDragEnd}>
      <SortableContext items={events} strategy={verticalListSortingStrategy}>
        <div
          className={cx(s.listWrapper, { [s.highlighted]: highlighted, [s.hidden]: isEmpty(events) })}
          ref={setNodeRef}
        >
          {events.map((tripEvent, i) => {
            const { getContent } = EVENT_OPTION_CONFIG[tripEvent.type];
            const isDraggable =
              events.length > 1 &&
              !(tripEvent.type === ETripEvent.Accommodation && tripEvent.timelineType === ETimelineType.Middle);

            return (
              <SortableEventWrapper
                id={tripEvent.id}
                key={tripEvent.id}
                renderItem={(dragProps) => (
                  <EventOption
                    id={tripEvent.id}
                    key={tripEvent.id + '_' + i}
                    icon={tripEvent.icon}
                    content={getContent(tripEvent)}
                    isDraggable={isDraggable}
                    dragProps={dragProps}
                    error={validateTripEvent(tripEvent)}
                  />
                )}
              />
            );
          })}
        </div>
      </SortableContext>
    </DndContext>
  );
};

const SortableEventWrapper = ({ renderItem, id }: IDraggableItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, setActivatorNodeRef, isDragging } = useSortable({
    id,
  });
  const style = {
    transform: isDragging
      ? CSS.Transform.toString({ ...transform, scaleY: 1 } as ITransform)
      : CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div className={s.listOptionWrapper} ref={setNodeRef} style={style} {...attributes}>
      {renderItem({ setActivatorNodeRef, listeners })}
    </div>
  );
};
